import type { Theme } from '@ms/yammer-web-core-ui/dist/util/theme/fluent9/';
import { create } from 'zustand';
export interface BrandThemes {
  readonly light: Theme;
  readonly dark: Theme;
}

export type TokensAndStylesType = BrandThemes | null;

export interface ActiveNetworkBranding {
  readonly id: string;
  readonly name: string;
  readonly colorScheme: string;
  readonly tokensAndStyles: TokensAndStylesType;
}

export type LogoTypes = 'SMALL' | 'LARGE';
interface BrandThemeState {
  readonly brandName: string | null | undefined;
  readonly brandSmallLogoUrl: string | null | undefined;
  readonly brandLargeLogoUrl: string | null | undefined;
  readonly brandTheme: ActiveNetworkBranding | null | undefined;
  readonly error: string | null;
  readonly hasLoaded: boolean;
  readonly viewerIsPreviewing: boolean;
  readonly isPreviewingNameOrLogo: boolean;

  setBrandName(brandName: string | null | undefined): void;
  setBrandLogoUrl(logoType: LogoTypes, url: string | null | undefined): void;
  setBrandTheme(brandTheme: ActiveNetworkBranding | null | undefined): void;
  setError(error: string | null | undefined): void;
  setViewerIsPreviewing(viewerIsPreviewing: boolean): void;
  setIsPreviewingNameOrLogo(isPreviewingNameOrLogo: boolean): void;
  resetState(): void;
}

export const useBrandThemeStore = create<BrandThemeState>((set) => ({
  brandName: null,
  brandSmallLogoUrl: null,
  brandLargeLogoUrl: null,
  brandTheme: null,
  error: null,
  hasLoaded: false,
  viewerIsPreviewing: false,
  isPreviewingNameOrLogo: false,

  setBrandName: (brandName) => {
    set({ brandName });
  },
  setBrandLogoUrl: (logoType: LogoTypes, url: string | null) => {
    set(logoType === 'SMALL' ? { brandSmallLogoUrl: url } : { brandLargeLogoUrl: url });
  },
  setBrandTheme: (brandTheme) => {
    set({
      brandTheme,
      error: null,
      hasLoaded: true,
    });
  },
  setError: (error) => {
    set({ error, hasLoaded: true });
  },
  setViewerIsPreviewing: (viewerIsPreviewing) => {
    set({ viewerIsPreviewing });
  },
  setIsPreviewingNameOrLogo: (isPreviewingNameOrLogo) => {
    set({ isPreviewingNameOrLogo });
  },
  resetState: () => {
    set({
      brandName: null,
      brandSmallLogoUrl: null,
      brandLargeLogoUrl: null,
      brandTheme: null,
      error: null,
      hasLoaded: false,
      viewerIsPreviewing: false,
      isPreviewingNameOrLogo: false,
    });
  },
}));

export const useBrandName = () => useBrandThemeStore((state) => state.brandName);
export const useBrandSmallLogoUrl = () => useBrandThemeStore((state) => state.brandSmallLogoUrl);
export const useBrandLargeLogoUrl = () => useBrandThemeStore((state) => state.brandLargeLogoUrl);
export const useBrandTheme = () => useBrandThemeStore((state) => state.brandTheme);
export const useBrandThemeError = () => useBrandThemeStore((state) => state.error);
export const useHasBrandThemeLoaded = () => useBrandThemeStore((state) => state.hasLoaded);
export const useViewerIsPreviewing = () => useBrandThemeStore((state) => state.viewerIsPreviewing);

export const useSetBrandName = () => useBrandThemeStore((state) => state.setBrandName);
export const useSetBrandLogoUrl = () => useBrandThemeStore((state) => state.setBrandLogoUrl);
export const useSetBrandTheme = () => useBrandThemeStore((state) => state.setBrandTheme);
export const useSetBrandThemeError = () => useBrandThemeStore((state) => state.setError);
export const useSetViewerIsPreviewing = () => useBrandThemeStore((state) => state.setViewerIsPreviewing);
export const useIsPreviewingNameOrLogo = () => useBrandThemeStore((state) => state.isPreviewingNameOrLogo);
export const useSetIsPreviewingNameOrLogo = () => useBrandThemeStore((state) => state.setIsPreviewingNameOrLogo);
